import React, { useState } from "react";
import { api, formatErr, STATUS_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadCloud, CheckCircle2, AlertTriangle, XCircle, FileCheck2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RECON_STYLE = {
  certa: { icon: CheckCircle2, cls: "text-emerald-600", label: "Associazione certa" },
  suggerita: { icon: AlertTriangle, cls: "text-amber-600", label: "Suggerita" },
  non_riconosciuto: { icon: XCircle, cls: "text-rose-600", label: "Non riconosciuto" },
};

export default function ImportWizard({ courseId, lessons, enrollments, onDone, initialLessonId = "" }) {
  const [lessonId, setLessonId] = useState(initialLessonId);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [replace, setReplace] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const enrollOptions = enrollments.map((e) => ({
    id: e.id, label: `${e.participant?.last_name} ${e.participant?.first_name}`,
  }));

  const doPreview = async () => {
    if (!lessonId || !file) { toast.error("Seleziona lezione e file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/courses/${courseId}/lessons/${lessonId}/import/preview`, fd);
      setPreview(data);
      const dec = {};
      data.reconciliation.forEach((r) => {
        dec[r.name] = {
          action: r.is_moderator ? "role" : (r.status === "non_riconosciuto" ? "exclude" : "confirm"),
          enrollment_id: r.enrollment_id || "",
        };
      });
      setDecisions(dec);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const setDec = (name, patch) => setDecisions((d) => ({ ...d, [name]: { ...d[name], ...patch } }));

  const confirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/imports/${preview.import_id}/confirm`, {
        decisions, replace_duplicates: replace,
      });
      toast.success(`Importazione confermata: ${data.inserted} accessi inseriti, ${data.skipped_duplicates} duplicati saltati.`);
      setPreview(null); setFile(null); setLessonId(""); onDone?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (preview) {
    return (
      <div className="space-y-5" data-testid="reconciliation-container">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display font-bold text-slate-900">Anteprima e riconciliazione</h3>
            <p className="text-sm text-slate-500">File: {preview.filename} · Separatore «{preview.delimiter}» · {preview.total_rows} righe</p>
          </div>
          <Button variant="outline" onClick={() => setPreview(null)}>Annulla</Button>
        </div>

        {preview.missing_columns.length > 0 && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">
            Colonne obbligatorie mancanti: {preview.missing_columns.join(", ")}
          </div>
        )}
        {preview.duplicate_file && (
          <div data-testid="duplicate-file-warning" className="rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm px-4 py-3 flex items-center gap-2">
            <AlertTriangle size={16} /> Questo file risulta già importato per questa lezione. Confermando senza "sostituisci" non verranno raddoppiate le ore.
          </div>
        )}
        {preview.duplicate_rows > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3">
            {preview.duplicate_rows} righe risultano già presenti e verranno saltate automaticamente.
          </div>
        )}

        <Card className="border-slate-200 overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50"><TableRow>
              <TableHead>Nominativo CSV</TableHead><TableHead>Accessi</TableHead>
              <TableHead>Esito</TableHead><TableHead>Azione</TableHead><TableHead>Partecipante</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {preview.reconciliation.map((r) => {
                const st = RECON_STYLE[r.status]; const Icon = st.icon;
                const dec = decisions[r.name] || {};
                return (
                  <TableRow key={r.name} data-testid={`recon-row-${r.name}`}>
                    <TableCell className="font-medium">
                      {r.name}
                      {r.is_moderator && <span className="ml-2 text-[10px] bg-slate-200 text-slate-600 rounded px-1.5 py-0.5">MODERATORE</span>}
                    </TableCell>
                    <TableCell>{r.session_count}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${st.cls}`}>
                        <Icon size={14} /> {st.label}{r.status === "suggerita" && ` (${Math.round(r.score * 100)}%)`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select value={dec.action} onValueChange={(v) => setDec(r.name, { action: v })}>
                        <SelectTrigger className="w-[150px] h-8" data-testid={`recon-action-${r.name}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirm">Associa</SelectItem>
                          <SelectItem value="alias">Associa + alias</SelectItem>
                          <SelectItem value="exclude">Escludi riga</SelectItem>
                          <SelectItem value="role">Docente/Tutor</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {["confirm", "alias"].includes(dec.action) && (
                        <Select value={dec.enrollment_id} onValueChange={(v) => setDec(r.name, { enrollment_id: v })}>
                          <SelectTrigger className="w-[200px] h-8" data-testid={`recon-enroll-${r.name}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            {enrollOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox checked={replace} onCheckedChange={setReplace} data-testid="replace-dup-checkbox" />
            Sostituisci eventuali righe duplicate (invece di saltarle)
          </label>
          <Button onClick={confirm} disabled={loading} data-testid="confirm-import-btn" className="bg-[#008C45] hover:bg-[#006B34]">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck2 size={16} className="mr-2" />}
            Conferma elaborazione
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className="p-6 border-slate-200 max-w-xl">
      <h3 className="font-display font-bold text-slate-900 mb-4">Carica report CSV della lezione</h3>
      <div className="space-y-4">
        <div>
          <Label>Lezione</Label>
          <Select value={lessonId} onValueChange={setLessonId}>
            <SelectTrigger data-testid="import-lesson-select"><SelectValue placeholder="Seleziona lezione…" /></SelectTrigger>
            <SelectContent>
              {lessons.map((l) => <SelectItem key={l.id} value={l.id}>Lezione {l.number} — {l.date ? l.date.split("-").reverse().join("/") : "s.d."} ({l.duration_minutes}′) {l.status === "svolta" ? "✓" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>File CSV</Label>
          <label data-testid="csv-dropzone" className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-[#008C45] transition-colors">
            <UploadCloud className="text-slate-400 mb-2" />
            <span className="text-sm text-slate-600">{file ? file.name : "Clicca per selezionare il file .csv"}</span>
            <input type="file" accept=".csv,text/csv" className="hidden" data-testid="csv-file-input"
              onChange={(e) => setFile(e.target.files[0])} />
          </label>
        </div>
        <Button onClick={doPreview} disabled={loading} data-testid="preview-import-btn" className="w-full bg-[#0B192C] hover:bg-[#07111E]">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Analizza anteprima
        </Button>
      </div>
    </Card>
  );
}
